import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaymentDetailSchema, PaymentSchema, SupplierInvoiceSchema } from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData, getPage, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { refreshAfterPosting } from './cache';
import { Facts, JournalRef, RecordFailure, RecordLink } from './detail';
import { businessToday } from './line-editor';
import { isAmount, toFils } from './money';
import { RefusalState } from './refusal';

/**
 * One supplier payment (`/payments/:id`): the supplier, the register the money left, the amount,
 * its allocations with a link to each invoice settled, the allocated and unallocated totals, the
 * status and, once posted, its journal (FRD-FIN-076; SDD §8.9). A direct link or a refresh opens
 * it; posting from here carries `If-Match`.
 */
export function PaymentDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canPost = hasCapability(session.data, 'finance.payment:post');
  const canSeeMovements = hasCapability(session.data, 'finance.bank_cash_transaction:view');
  const detail = useQuery({
    queryKey: ['payment', id],
    queryFn: () => getData(`/api/v1/finance/payments/${id}`, PaymentDetailSchema),
  });
  const post = useMutation({
    mutationFn: (payment: { id: string; version: number }) =>
      postJson(
        `/api/v1/finance/payments/${payment.id}/post`,
        {},
        PaymentSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: payment.version },
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });
  // FRD-FIN-078: a posted payment's advance is offset against a later invoice of its supplier.
  const [applying, setApplying] = useState(false);
  const [target, setTarget] = useState('');
  const [applyAmount, setApplyAmount] = useState('');
  const supplierId = detail.data?.payment.supplierId ?? null;
  const openInvoices = useQuery({
    queryKey: ['supplier-invoices', 'open', supplierId],
    enabled: applying && supplierId !== null,
    queryFn: () =>
      getPage(
        `/api/v1/finance/supplier-invoices?pageSize=100&filter[supplier_id]=${supplierId ?? ''}` +
          '&filter[status]=posted&filter[open]=true',
        SupplierInvoiceSchema,
      ),
  });
  const apply = useMutation({
    mutationFn: () =>
      postJson(
        `/api/v1/finance/payments/${id}/apply-advance`,
        {
          supplierInvoiceId: target,
          allocatedAmount: applyAmount.trim(),
          allocationDate: businessToday(session.data?.businessTimezone),
        },
        PaymentDetailSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setApplying(false);
      await refreshAfterPosting(queryClient);
    },
  });

  const back = (
    <Button component={RouterLink} to="/payments" startIcon={<ArrowBackIcon />}>
      All payments
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the payment" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Supplier payment" noun="payment" error={detail.error} back={back} />
    );
  }

  const { payment, allocations, register } = detail.data;
  const remainder = toFils(payment.unallocatedAmount) !== 0n;
  const supplier = detail.data.supplier?.summary ?? null;
  // A payment to an employee settles expense claims (MD-09; FRD-FIN-088).
  const employee = detail.data.employee?.summary ?? null;
  const toEmployee = payment.payeeType === 'employee';
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${toEmployee ? 'Employee' : 'Supplier'} payment ${payment.documentNo}`}
        {...((toEmployee ? employee : supplier) === null
          ? {}
          : { description: (toEmployee ? employee : supplier) ?? '' })}
        actions={
          <>
            {back}
            {canPost && payment.status === 'draft' && (
              <Button
                variant="contained"
                disabled={post.isPending}
                onClick={() => {
                  post.mutate({ id: payment.id, version: payment.version });
                }}
              >
                {post.isPending ? 'Posting…' : 'Post'}
              </Button>
            )}
            {canPost && payment.status === 'posted' && remainder && !toEmployee && (
              <Button
                variant="outlined"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setApplying(true);
                }}
              >
                Apply advance
              </Button>
            )}
          </>
        }
      />
      {payment.status === 'draft' && remainder && !toEmployee && (
        <Alert severity="info" variant="outlined">
          {payment.unallocatedAmount} is not allocated. When the payment is posted it is held as a
          supplier advance, to be offset against later invoices of this supplier (FRD-FIN-078).
        </Alert>
      )}
      {post.isError && <RefusalState error={post.error} />}
      <Section title="Payment">
        <Facts
          label="Payment details"
          facts={[
            ['Status', <StatusChip key="s" status={payment.status} />],
            toEmployee
              ? ['Employee', employee ?? 'Not visible']
              : ['Supplier', supplier ?? 'Not visible'],
            [
              'Paid from',
              register === null ? 'Not visible' : `${register.code} — ${register.name}`,
            ],
            ['Payment date', payment.paymentDate],
            ['Reference', payment.reference ?? '—'],
            ['Description', payment.description ?? '—'],
            ['Amount', payment.amount],
            ['Allocated', payment.allocatedAmount],
            [
              payment.status === 'posted' ? 'Held as supplier advance' : 'Unallocated',
              payment.unallocatedAmount,
            ],
            ['Journal', <JournalRef key="journal" journal={detail.data.journal} />],
            [
              'Bank/cash movement',
              canSeeMovements ? (
                <RecordLink key="m" to={`/bank-cash/movements/${payment.bankCashTransactionId}`}>
                  View the movement
                </RecordLink>
              ) : (
                'Not visible'
              ),
            ],
          ]}
        />
      </Section>
      <Section title="Allocations">
        {allocations.length === 0 ? (
          <EmptyState title="Nothing allocated" />
        ) : (
          <Table size="small" aria-label="Allocations">
            <TableHead>
              <TableRow>
                <TableCell>{toEmployee ? 'Expense claim' : 'Invoice'}</TableCell>
                <TableCell>{toEmployee ? '' : 'Supplier reference'}</TableCell>
                <TableCell>Allocation date</TableCell>
                <TableCell align="right">Allocated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allocations.map((allocation) => (
                <TableRow key={allocation.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {allocation.expenseClaimId != null ? (
                      <RecordLink to={`/expense-claims/${allocation.expenseClaimId}`}>
                        {allocation.claimDocumentNo ?? 'Open the claim'}
                      </RecordLink>
                    ) : allocation.supplierInvoiceId === null ? (
                      '—'
                    ) : (
                      <RecordLink to={`/supplier-invoices/${allocation.supplierInvoiceId}`}>
                        {allocation.invoiceDocumentNo ?? 'Open the invoice'}
                      </RecordLink>
                    )}
                  </TableCell>
                  <TableCell>{allocation.supplierInvoiceNumber ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{allocation.allocationDate}</TableCell>
                  <TableCell align="right">{allocation.allocatedAmount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {applying && (
        <FormDialog
          open
          title="Apply the advance to an invoice"
          submitLabel="Apply"
          canSubmit={target !== '' && isAmount(applyAmount) && toFils(applyAmount) > 0n}
          submitting={apply.isPending}
          error={apply.isError ? apply.error.message : null}
          onClose={() => {
            setApplying(false);
          }}
          onSubmit={() => {
            apply.mutate();
          }}
        >
          <TextField
            select
            label="Invoice"
            required
            value={target}
            onChange={(e) => {
              setTarget(e.target.value);
              const invoice = openInvoices.data?.items.find((i) => i.id === e.target.value);
              if (invoice !== undefined) {
                setApplyAmount(
                  toFils(invoice.outstandingAmount) < toFils(payment.unallocatedAmount)
                    ? invoice.outstandingAmount
                    : payment.unallocatedAmount,
                );
              }
            }}
          >
            {(openInvoices.data?.items ?? []).map((invoice) => (
              <MenuItem key={invoice.id} value={invoice.id}>
                {invoice.documentNo} ({invoice.supplierInvoiceNumber}) — outstanding{' '}
                {invoice.outstandingAmount}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Amount"
            required
            value={applyAmount}
            onChange={(e) => {
              setApplyAmount(e.target.value);
            }}
          />
        </FormDialog>
      )}
      <DocumentsPanel
        recordType="finance.payment"
        recordId={payment.id}
        canAttach={
          hasCapability(session.data, 'finance.payment:create') ||
          hasCapability(session.data, 'finance.payment:change')
        }
        title="Supporting documents"
      />
      <RecordHistory recordType="finance.payment" recordId={payment.id} />
    </Stack>
  );
}
